import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axiosInstance from "../../helpers/axiosInstance";
import Dropzone from "react-dropzone";

const EcommerceCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    image: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [breadcrumbItems] = useState([
    { title: "Ecommerce", link: "#" },
    { title: "Categories", link: "#" },
  ]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("api/v1/categories");
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    setModal(!modal);
    if (!modal) {
      setFormData({ name: "", image: null });
      setPreviewImage(null);
      setEditMode(false);
      setSelectedCategory(null);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const uploadImageToOm = async (file) => {
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("image", file);
      const response = await axiosInstance.post(
        "api/v1/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.images;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    try {
      setPreviewImage(URL.createObjectURL(file));
      const imageUrl = await uploadImageToOm(file);
      setFormData((prev) => ({
        ...prev,
        image: imageUrl,
      }));
    } catch (error) {
      alert("Failed to upload image. Please try again.");
      setPreviewImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editMode) {
        await axiosInstance.put(
          `api/v1/admin/category/${selectedCategory._id}`,
          formData
        );
      } else {
        await axiosInstance.post("api/v1/admin/category", formData);
      }
      fetchCategories();
      toggle();
    } catch (error) {
      console.error("Error saving category:", error);
      alert(error.response?.data?.message || "Error saving category");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      image: category.image,
    });
    setPreviewImage(category.image);
    setEditMode(true);
    setModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        setLoading(true);
        await axiosInstance.delete(`api/v1/admin/category/${categoryId}`);
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
        alert(error.response?.data?.message || "Error deleting category");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Categories" breadcrumbItems={breadcrumbItems} />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <CardTitle className="h4">Category Management</CardTitle>
                  <Button color="primary" onClick={toggle}>
                    Add New Category
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive max-w-full">
                    <Table className="table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr className="w-full">
                          <th>Image</th>
                          <th>Name</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((category) => (
                          <tr key={category._id}>
                            <td>
                              <img
                                src={category.image.startsWith("https") ? category.image : `${process.env.REACT_APP_BASEURL}${category.image}`}
                                alt={category.name}
                                className="rounded"
                                height="48"
                                width="56"
                              />
                            </td>
                            <td>{category.name}</td>
                            <td>
                              <Button
                                color="info"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(category)}
                              >
                                Edit
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDelete(category._id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modal} toggle={toggle} size="lg">
          <ModalHeader toggle={toggle}>
            {editMode ? "Edit Category" : "Add New Category"}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="name">Category Name</Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  placeholder="Enter category name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Category Image</Label>
                <div className="dropzone">
                  <Dropzone
                    onDrop={handleImageDrop}
                    accept={{
                      "image/*": [".png", ".jpg", ".jpeg"],
                    }}
                    multiple={false}
                    disabled={uploadingImage}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div
                        className="dz-message needsclick"
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="mb-3">
                          <i className="display-4 text-muted ri-upload-cloud-2-line" />
                        </div>
                        <h4>
                          {uploadingImage
                            ? "Uploading image..."
                            : "Drop an image here or click to upload."}
                        </h4>
                      </div>
                    )}
                  </Dropzone>
                </div>
                {previewImage && (
                  <div className="mt-3">
                    <img
                      src={previewImage.startsWith("https") ? previewImage : `${process.env.REACT_APP_BASEURL}${previewImage}`}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "contain",
                      }}
                      className="img-fluid"
                    />
                  </div>
                )}
              </FormGroup>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  color="light"
                  onClick={toggle}
                  className="me-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={loading || uploadingImage || !formData.image}
                >
                  {loading ? <Spinner size="sm" /> : "Save Category"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default EcommerceCategories;
